import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

/**
 * Unified approval workflow hook for entities with approval/institutionalization status.
 * Replaces 24+ duplicated approval mutations across Dashboard, Artifacts, Issues pages.
 * 
 * Supports: Artifact, Ritual, Policy, Capability, Objective
 * 
 * Usage:
 * const { approve, reject, institutionalize, isApproving, ... } = useApprovalWorkflow({
 *   entityType: 'Artifact',
 *   userEmail: user?.email,
 *   onSuccess: () => handleClose()
 * });
 * 
 * // Then:
 * <Button onClick={() => approve(artifact.id)}>Approve</Button>
 */

// Status flow for different entity types
export const STATUS_FLOWS = {
  Artifact: {
    initial: 'draft',
    pendingApproval: 'pending_approval',
    approved: 'approved',
    institutionalized: 'institutionalized',
    queryKey: 'artifacts'
  },
  Ritual: {
    initial: 'draft',
    pendingApproval: 'pending_approval',
    approved: 'approved',
    institutionalized: 'institutionalized',
    queryKey: 'rituals'
  },
  Policy: {
    initial: 'draft',
    pendingApproval: 'pending_approval',
    approved: 'approved',
    institutionalized: 'institutionalized',
    queryKey: 'policies'
  },
  Capability: {
    initial: 'requested',
    pendingApproval: 'in_development',
    approved: 'approved',
    institutionalized: 'institutionalized',
    queryKey: 'capabilities'
  },
  Objective: {
    initial: 'pending_activation',
    approved: 'active',
    completed: 'completed',
    archived: 'archived',
    queryKey: 'objectives'
  }
};

export default function useApprovalWorkflow({
  entityType,
  userEmail,
  onSuccess,
  onError
}) {
  const queryClient = useQueryClient();
  const flow = STATUS_FLOWS[entityType];
  
  const getEntity = () => base44.entities[entityType];

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: flow.approved,
        approved_by: userEmail,
        approved_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to approve ${entityType}:`, error);
      onError?.(error);
    }
  });

  // Reject mutation (send back to draft/initial)
  const rejectMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: flow.initial
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to reject ${entityType}:`, error);
      onError?.(error);
    }
  });

  // Institutionalize mutation
  const institutionalizeMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: flow.institutionalized,
        institutionalized_by: userEmail,
        institutionalized_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to institutionalize ${entityType}:`, error);
      onError?.(error);
    }
  });

  // Request approval mutation (move from draft to pending)
  const requestApprovalMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: flow.pendingApproval
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to request approval for ${entityType}:`, error);
      onError?.(error);
    }
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: 'archived',
        archived_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to archive ${entityType}:`, error);
      onError?.(error);
    }
  });

  // Unarchive mutation
  const unarchiveMutation = useMutation({
    mutationFn: async (id) => {
      const entity = getEntity();
      return entity.update(id, {
        status: flow.approved || flow.initial,
        archived_at: null
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to unarchive ${entityType}:`, error);
      onError?.(error);
    }
  });

  if (!flow) {
    console.error(`Unknown entity type for approval workflow: ${entityType}`);
    return {
      approve: () => {}, reject: () => {}, institutionalize: () => {},
      requestApproval: () => {}, archive: () => {}, unarchive: () => {},
      approveAsync: () => Promise.resolve(), rejectAsync: () => Promise.resolve(),
      institutionalizeAsync: () => Promise.resolve(),
      isApproving: false, isRejecting: false, isInstitutionalizing: false,
      isRequestingApproval: false, isArchiving: false, isUnarchiving: false, isLoading: false,
      flow: null, canApprove: () => false, canInstitutionalize: () => false,
      canArchive: () => false, isApproved: () => false, isInstitutionalized: () => false
    };
  }

  return {
    // Actions
    approve: approveMutation.mutate,
    reject: rejectMutation.mutate,
    institutionalize: institutionalizeMutation.mutate,
    requestApproval: requestApprovalMutation.mutate,
    archive: archiveMutation.mutate,
    unarchive: unarchiveMutation.mutate,
    
    // Async versions
    approveAsync: approveMutation.mutateAsync,
    rejectAsync: rejectMutation.mutateAsync,
    institutionalizeAsync: institutionalizeMutation.mutateAsync,
    
    // Loading states
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isInstitutionalizing: institutionalizeMutation.isPending,
    isRequestingApproval: requestApprovalMutation.isPending,
    isArchiving: archiveMutation.isPending,
    isUnarchiving: unarchiveMutation.isPending,
    isLoading: approveMutation.isPending || rejectMutation.isPending || 
               institutionalizeMutation.isPending || archiveMutation.isPending,
    
    // Status flow info
    flow,
    
    // Helpers
    canApprove: (item) => item?.status === flow.initial || item?.status === flow.pendingApproval,
    canInstitutionalize: (item) => item?.status === flow.approved,
    canArchive: (item) => item?.status !== 'archived',
    isApproved: (item) => item?.status === flow.approved,
    isInstitutionalized: (item) => item?.status === flow.institutionalized
  };
}

/**
 * Batch approval for multiple items
 */
export function useBatchApproval({ entityType, userEmail, onSuccess }) {
  const queryClient = useQueryClient();
  const flow = STATUS_FLOWS[entityType];

  const batchApproveMutation = useMutation({
    mutationFn: async (ids) => {
      const entity = base44.entities[entityType];
      const updates = ids.map(id => 
        entity.update(id, {
          status: flow.approved,
          approved_by: userEmail,
          approved_at: new Date().toISOString()
        })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [flow.queryKey] });
      onSuccess?.();
    }
  });

  return {
    batchApprove: batchApproveMutation.mutate,
    isBatchApproving: batchApproveMutation.isPending
  };
}